import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { getInvoiceProviders } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import { FilterConfig } from "types/Common";

export default function useInvoiceFilters() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { permissions, getPaymentLocationLabel } = usePermissions();
  const paymentLocations = useSortedPaymentLocations();
  const [providerFilterOptions, setProviderFilterOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.invoicing.view_invoices) navigate("/access-denied");
    else {
      getFilterProviderOptions();
    }
  }, []);

  const getFilterProviderOptions = () => {
    getInvoiceProviders()
      .then((res) => {
        const providersData: { id: number; company_name: string }[] = res.data;
        setProviderFilterOptions(
          providersData.map((provider) => ({ value: provider.id.toString(), label: provider.company_name })),
        );
      })
      .catch((err) => errorToast(err));
  };

  const INVOICE_DISPLAY_STATUS = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "billing.awaitingAudit", defaultMessage: "Awaiting Audit" }),
        value: "awaiting_audit",
      },
      {
        label: intl.formatMessage({ id: "general.approved", defaultMessage: "Approved" }),
        value: "approved",
      },
      {
        label: intl.formatMessage({ id: "general.declined", defaultMessage: "Declined" }),
        value: "declined",
      },
    ],
    [intl],
  );

  const config: FilterConfig[] = [
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.displayStatus", defaultMessage: "Display status" }),
      key: "display_status",
      options: INVOICE_DISPLAY_STATUS,
    },
    {
      filterType: "multiselect",
      label: getPaymentLocationLabel(),
      key: "payment_location",
      isSearchable: true,
      options: paymentLocations,
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.provider", defaultMessage: "Provider" }),
      key: "provider",
      isSearchable: true,
      options: providerFilterOptions,
    },
  ];

  return { config };
}
